import { useQuery } from '@tanstack/react-query';

import type { RegionNode } from '../../api/regions';
import { fetchWorkshopTypes, type WorkshopType } from '../../api/workshop-types';
import { useWritableRegions } from '../dashboards/primitives/use-writable-regions';
import { fetchAllActiveVenueRoster } from '../dashboards/venues/roster/venues-roster-api';

import type { VenueRow } from './private-event-venues';

const STALE_60S = 60_000;

export type VenuesStatus = 'loading' | 'failed' | 'ready';

export interface PrivateEventOptions {
  types: WorkshopType[];
  /** The regions the caller may write to, as a tree. */
  regions: RegionNode[];
  regionsLoading: boolean;
  regionsFailed: boolean;
  /** Every active venue; scoped to a region by `toVenueOptions`. */
  venueRows: VenueRow[];
  venuesStatus: VenuesStatus;
  retryVenues: () => void;
}

/**
 * Choices for the create drawer. Regions come from the same writable-region
 * scope the Create Workshop drawer uses, so a region offered here can never
 * come back as an out-of-scope refusal. Venues are the active roster under
 * their own cache key, leaving the workshop drawer's venue list untouched.
 * A workshop-type failure leaves the field empty rather than blanking the drawer.
 */
export function usePrivateEventOptions(): PrivateEventOptions {
  const { regions, loading, failed } = useWritableRegions();
  const typesQuery = useQuery({ queryKey: ['workshop-types'], queryFn: fetchWorkshopTypes });
  const venuesQuery = useQuery({
    queryKey: ['venues', 'private-event-create'],
    queryFn: fetchAllActiveVenueRoster,
    staleTime: STALE_60S,
  });
  const venuesStatus: VenuesStatus = venuesQuery.isError
    ? 'failed'
    : venuesQuery.isPending
      ? 'loading'
      : 'ready';
  return {
    types: typesQuery.data ?? [],
    regions,
    regionsLoading: loading,
    regionsFailed: failed,
    venueRows: venuesQuery.data ?? [],
    venuesStatus,
    retryVenues: () => void venuesQuery.refetch(),
  };
}
